import { useAppUpdateStore } from '@/lib/appUpdate'
import { useIsOnline } from '@/lib/connectivity'
import { useQueueDepth } from '@/lib/queue'
import { describeAge, SNAPSHOT_STALE_AFTER, useSnapshotAge } from '@/lib/snapshot'

/**
 * What the app knows about its connection, in the shell, the whole time
 * (issue #64) — not only at the moment something fails.
 *
 * Somebody counting kit in a cellar for an hour needs to be able to see that
 * the app knows it is offline and is showing them what it saved, because the
 * alternative is a screen that looks exactly like a working one until they try
 * to change something. Nothing is shown while the connection is fine: a chip
 * that is always there is furniture, and furniture is not read.
 *
 * The brass secondary is the reserved attention colour, and being cut off is an
 * attention state in the same sense an overdue PAT is — something is wrong and
 * the person needs to know before they act on the screen, not after.
 */
export function ConnectionStatus() {
  const online = useIsOnline()
  const queued = useQueueDepth()
  const age = useSnapshotAge()
  const updateReady = useAppUpdateStore((s) => s.ready)
  const applyUpdate = useAppUpdateStore((s) => s.apply)

  /**
   * How old the device's copy of the inventory is (issue #70), said out loud in
   * exactly two situations and silent otherwise, which is the rule the rest of
   * this component follows.
   *
   * **Offline**, because "what was saved on this device" is only useful with a
   * date on it — a walker deciding whether to trust a quantity needs to know
   * whether it was fetched four minutes or four days ago.
   *
   * **Online but stale**, because that means the app believes it can reach the
   * API and has not managed to for three quarters of an hour. Nothing is broken
   * yet and nothing has been lost; what is at stake is somebody carrying the
   * tablet downstairs on the assumption it is current. That is an attention
   * state in the same sense an overdue PAT is, which is what the brass is for.
   */
  const stale = age !== null && age > SNAPSHOT_STALE_AFTER
  const saved = age === null ? null : `saved ${describeAge(age)}`

  return (
    // Announced rather than merely drawn: going offline mid-walk is a change of
    // state nobody asked for, and it happens while somebody is looking at a
    // shelf rather than at the screen.
    <div className="connection-status" role="status" aria-live="polite">
      {!online && (
        <span
          className="status-chip is-attention"
          title={
            saved === null
              ? 'Showing what was saved on this device.'
              : `Showing what was saved on this device, ${describeAge(age ?? 0)}.`
          }
        >
          Offline
        </span>
      )}

      {!online && saved !== null && (
        <span className="status-chip is-saved" title="When this device last caught up.">
          {saved}
        </span>
      )}

      {online && stale && saved !== null && (
        <span
          className="status-chip is-attention"
          title="This device has not managed to bring the inventory up to date. It will keep trying."
        >
          Offline copy {saved}
        </span>
      )}

      {/*
        The depth of the queue, the whole time it has anything in it — not only
        at sync (issue #64). Somebody counting for an hour needs to be able to
        see that the app is holding forty changes and has not lost them.
      */}
      {queued > 0 && (
        <span
          className="status-chip is-queued"
          title="Changes made on this device, waiting to be sent."
        >
          {queued} to send
        </span>
      )}

      {updateReady && (
        <button
          type="button"
          className="status-chip is-update"
          onClick={applyUpdate}
          title="A newer version of the Tech Inventory has been downloaded."
        >
          Update ready
        </button>
      )}
    </div>
  )
}
